import { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import { selectUserDetail, userDetail } from "../../redux/slice/userSlice";
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import {
  DocumentUpload,
  httpGet,
  httpPost,
} from "../../services/commonService";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";
import CustomMultiSelectDropdown from "../../shared-components/form-controls/customMultiSelectDropdown";
import CustomAutoCompleteDropdown from "../../shared-components/form-controls/customAutoCompleteDropdown";
import CustomTextFieldComponent from "../../shared-components/form-controls/customTextField";
import CustomPhoneField from "../../shared-components/form-controls/customPhoneField";
import { getPhoneFieldValueFromRawNumber } from "../../shared-components/phoneHelper";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import CustomImageUpload from "../../shared-components/customImageUpload";
import i18n from "../../i18n";
import "react-quill-new/dist/quill.snow.css";

const formats = [
  "bold",
  "italic",
  "underline",
  "align",
  "list", // only "list"
  "indent",
  "link",
];

const modules = {
  toolbar: {
    container: [
      ["bold", "italic", "underline"],
      ["undo", "redo"], // These will be your custom buttons, not native Quill buttons
      ["link"],
      [
        { align: "" },
        { align: "center" },
        { align: "right" },
        { align: "justify" },
      ],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
    ],
    handlers: {
      undo: function () {
        this.quill.history.undo();
      },
      redo: function () {
        this.quill.history.redo();
      },
    },
  },
};

const lastNameValidator = (value) => {
  if (!value) {
    return i18n.t("PublicInfo.last name") + i18n.t("Required");
  } else if (!dataConstant.NoSpecialCharacterRegex.test(value)) {
    return i18n.t("SpecialCharacter") + i18n.t("PublicInfo.last name");
  } else if (value.trim() === "") {
    return i18n.t("Whitespace") + i18n.t("PublicInfo.last name");
  }
  return true;
};

const firstNameValidator = (value) => {
  if (!value) {
    return i18n.t("PublicInfo.first name") + i18n.t("Required");
  } else if (!dataConstant.NoSpecialCharacterRegex.test(value)) {
    return i18n.t("SpecialCharacter") + i18n.t("PublicInfo.first name");
  } else if (value.trim() === "") {
    return i18n.t("Whitespace") + i18n.t("first name");
  }
  return true;
};
const PublicInformation = () => {
  const dispatch = useDispatch();
  const getLoggedUserDetails = useSelector(selectUserDetail);
  const authenticationState = useContext(AuthenticationContext);
  const { t } = useTranslation();
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [state, setState] = useState({
    publicInfo: {
      IsYourselfMentor: {},
      IsMentor: false,
      profileIcon: [],
      fileName: "",
      defaultFiles: [],
      profileURL: dataConstant.UserImageURL,
      IsYourselfReinMentor: {},
      IsReinMentor: false,
      reinDescription: "",
      IsSecurityMentor: false,
      securityDescription: "",
      languageData: [],
      stateData: [],
      makeYourselfMentorStatusData: [],
      makeYourselfReinMentorStatusData: [],
      makeYourselfSecurityMentorStatusData: [],
    },
    isMentorStatusDataCalled: false,
    isDropDownApiCalled: false,
    isStateDropDownCalled: false,
    toDeleteImage: false,
    imageDisplay: true,
    fileProfileIconImage: null,
    isProfileChanged: false,
    phoneCountryCodeList: [],
  });
  const [isImageValid, setIsImageValid] = useState(true);
  const [isPhoneFiledForceUpdate, setIsPhoneFiledForceUpdate] = useState(true);
  const [businessTypeDisabledStatus, setBusinessTypeDisabledStatus] = useState({
    ReinH2O: false,
    Solar: false,
    HomeServices: false,
  });
  const imageUploadRef = useRef(null);

  const methods = useForm({
    mode: "all",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      phoneNumber: "",
      phoneNumberAlt: "",
      noOfReinH2OCloseLeads: "",
      noOfSolarCloseLeads: "",
      noOfClosedSecurityLeads: "",
      LeadsToBecomeSolarMentor: "",
      LeadsToBecomeReinMentor: "",
      LeadsToBecomeSecurityMentor: "",
      rainH2OLeadAllowedFlag: {},
      solarLeadAllowedFlag: {},
      securityLeadAllowedFlag: {},
      language: [],
      state: [],
      solarDescription: "",
      reinDescription: "",
      securityDescription: "",
      freedomEmail: "",
    },
  });

  const {
    setValue,
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    getAllDropDownList();
    GetYourselfMentorStatusList();
    getStateDropDownList();
    getBusinessTypes();
  }, []);

  useEffect(() => {
    if (
      state.isDropDownApiCalled &&
      state.isMentorStatusDataCalled &&
      state.isStateDropDownCalled
    ) {
      getUserDetailsById();
    }
  }, [
    state.isDropDownApiCalled,
    state.isMentorStatusDataCalled,
    state.isStateDropDownCalled,
  ]);

  const handleImageValidation = (isValid) => {
    setIsImageValid(isValid);
  };
  const getBusinessTypes = async () => {
    await httpGet(
      urlConstant.Common.GetBusinessTypeDropDownList,
      true,
      false,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        const businessTypes = response.Value.Data;
        const disabledStatus = {};
        businessTypes.forEach(({ label, isDisabled }) => {
          disabledStatus[label] = isDisabled;
        });
        setBusinessTypeDisabledStatus({
          ReinH2O: disabledStatus["ReinH2O"] ?? false,
          Solar: disabledStatus["Solar"] ?? false,
          HomeServices: disabledStatus["Home Services"] ?? false,
        });
      })
      .catch((err) => {
        console.error(err);
      });
  };
  const formConfig = {
    languageParamValueId: {
      label: t("PublicInfo.SelectLanguagesLabel"),
      name: "language",
      dropDownList: state.publicInfo.languageData,
    },
    stateParamValueId: {
      label: t("PublicInfo.SelectStatesLabel"),
      name: "state",
      dropDownList: state.publicInfo.stateData,
    },
    isBecomeSolarParamValueId: {
      label: t("PublicInfo.MakeYourselfMentor"),
      name: "solarLeadAllowedFlag",
      dropDownList: state.publicInfo.makeYourselfMentorStatusData,
    },
    isBecomeReinParamValueId: {
      label: t("PublicInfo.MakeYourselfReinMentor"),
      name: "rainH2OLeadAllowedFlag",
      dropDownList: state.publicInfo.makeYourselfReinMentorStatusData,
    },
    isBecomeSecurityParamValueId: {
      label: t("PublicInfo.MakeYourselfHomeServicesMentor"),
      name: "securityLeadAllowedFlag",
      dropDownList: state.publicInfo.makeYourselfSecurityMentorStatusData,
    },
    firstNameParamValueId: {
      name: "firstName",
      label: t("PublicInfo.First Name"),
      required: true,
      type: "text",
      maxLength: 100,
      validation: {
        validate: firstNameValidator,
      },
    },
    lastNameParamValueId: {
      name: "lastName",
      label: t("PublicInfo.Last Name"),
      required: true,
      type: "text",
      maxLength: 100,
      validation: {
        validate: lastNameValidator,
      },
    },
    companyParamValueId: {
      name: "company",
      label: t("PublicInfo.Company"),
      type: "text",
      maxLength: 100,
    },
    emailParamValueId: {
      name: "email",
      label: t("PublicInfo.Email Address"),
      type: "email",
      maxLength: 100,
      required: true,
    },
    becomeSolarParamValueId: {
      name: "LeadsToBecomeSolarMentor",
      label: t("PublicInfo.LeadsToBecomeMentor"),
      type: "text",
      isDisabled: true,
    },
    noOfSolarLeadParamValueId: {
      name: "noOfSolarCloseLeads",
      label: t("PublicInfo.NumberOfClosedLeads"),
      type: "text",
      isDisabled: true,
    },
    becomeReinParamValueId: {
      name: "LeadsToBecomeReinMentor",
      label: t("PublicInfo.LeadsToBecomeReinMentor"),
      type: "text",
      isDisabled: true,
    },
    noOfReinLeadParamValueId: {
      name: "noOfReinH2OCloseLeads",
      label: t("PublicInfo.NumberOfClosedReinLeads"),
      type: "text",
      isDisabled: true,
    },
    becomeSecurityParamValueId: {
      name: "LeadsToBecomeSecurityMentor",
      label: t("PublicInfo.LeadsToBecomeHomeServicesMentor"),
      type: "text",
      isDisabled: true,
    },
    noOfSecurityLeadParamValueId: {
      name: "noOfClosedSecurityLeads",
      label: t("PublicInfo.NumberOfClosedHomeServicesLeads"),
      type: "text",
      isDisabled: true,
    },
    freedomEmailParamValueId: {
      name: "freedomEmail",
      label: t("PublicInfo.Freedom Email"),
      type: "email",
      maxLength: 100,
      required: true,
    },
  };

  const getUserDetailsById = () => {
    const getPublicInfoURL = urlConstant.User.GetPublicInfoById;
    httpGet(
      getPublicInfoURL,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    ).then((response) => {
      if (response.Value.Status === dataConstant.APIResponseStatus.Success) {
        const userPublicDetail = response.Value.Data;
        setValue("firstName", userPublicDetail.FirstName);
        setValue("lastName", userPublicDetail.LastName);
        setValue("email", userPublicDetail.PublicEmail);
        setValue("company", userPublicDetail.Company);
        setValue("noOfSolarCloseLeads", userPublicDetail.NoOfClosedLeads);
        setValue("noOfReinH2OCloseLeads", userPublicDetail.NoOfClosedReinLeads);
        setValue(
          "noOfClosedSecurityLeads",
          userPublicDetail.NoOfClosedSecurityLeads
        );
        setValue("solarDescription", userPublicDetail.Description);
        setValue("reinDescription", userPublicDetail.ReinDescription);
        setValue("securityDescription", userPublicDetail.SecurityDescription);
        setValue("freedomEmail", userPublicDetail.FreedomEmail);
        const selectedLanguageList = [];
        if (userPublicDetail.LanguageIds.Data.length > 0) {
          userPublicDetail.LanguageIds.Data.forEach((item) => {
            const getLanguage = state.publicInfo.languageData.find(
              (states) => states.value === item.AttributeId
            );
            if (getLanguage !== undefined) {
              selectedLanguageList.push(getLanguage);
            }
          });
        }
        if (selectedLanguageList.length > 0)
          setValue("language", selectedLanguageList);
        const selectedStateList = [];
        if (userPublicDetail.StateIds.Data.length > 0) {
          userPublicDetail.StateIds.Data.forEach((item) => {
            const getstate = state.publicInfo.stateData.find(
              (language) => language.value === item.AttributeId
            );
            if (getstate !== undefined) {
              selectedStateList.push(getstate);
            }
          });
        }
        if (selectedStateList.length > 0) setValue("state", selectedStateList);
        let getYourSelfMentor = null;
        if (userPublicDetail.IsYourselfMentor !== null) {
          getYourSelfMentor =
            state.publicInfo.makeYourselfMentorStatusData.find(
              (item) => item.value === String(userPublicDetail.IsYourselfMentor)
            );
          if (getYourSelfMentor !== undefined)
            setValue("solarLeadAllowedFlag", getYourSelfMentor);
        }
        let getYourSelfReinMentor = null;
        if (userPublicDetail.IsYourselfReinMentor !== null) {
          getYourSelfReinMentor =
            state.publicInfo.makeYourselfReinMentorStatusData.find(
              (item) =>
                item.value === String(userPublicDetail.IsYourselfReinMentor)
            );
          if (getYourSelfReinMentor !== undefined) {
            setValue("rainH2OLeadAllowedFlag", getYourSelfReinMentor);
          }
        }
        let getYourSelfSecurityMentor = null;
        if (userPublicDetail.IsYourselfSecurityMentor !== null) {
          getYourSelfSecurityMentor =
            state.publicInfo.makeYourselfSecurityMentorStatusData.find(
              (item) =>
                item.value === String(userPublicDetail.IsYourselfSecurityMentor)
            );
          if (getYourSelfSecurityMentor !== undefined) {
            setValue("securityLeadAllowedFlag", getYourSelfSecurityMentor);
          }
        }
        const leadToBecomeMentor =
          parseInt(userPublicDetail.NoOfClosedLeads, 10) > 4
            ? 0
            : 5 - parseInt(userPublicDetail.NoOfClosedLeads, 10);
        const leadToBecomeReinMentor =
          parseInt(userPublicDetail.NoOfClosedReinLeads, 10) > 4
            ? 0
            : 5 - parseInt(userPublicDetail.NoOfClosedReinLeads, 10);
        const leadsToBecomeSecurityMentor =
          5 - parseInt(userPublicDetail.NoOfClosedSecurityLeads) >= 0
            ? 5 - parseInt(userPublicDetail.NoOfClosedSecurityLeads)
            : 0;
        setValue("LeadsToBecomeSolarMentor", leadToBecomeMentor);
        setValue("LeadsToBecomeReinMentor", leadToBecomeReinMentor);
        setValue("LeadsToBecomeSecurityMentor", leadsToBecomeSecurityMentor);
        let defaultFileValue = [];
        if (userPublicDetail.StoredPath === null) {
          defaultFileValue = [];
        } else {
          defaultFileValue = [
            {
              id: 1,
              images: userPublicDetail.StoredPath,
              originalFileName: userPublicDetail.OriginalFileName,
            },
          ];
        }
        setState((prevState) => ({
          ...prevState,
          publicInfo: {
            ...prevState.publicInfo,
            IsMentor: userPublicDetail.IsMentor,
            IsReinMentor: userPublicDetail.IsReinMentor,
            IsYourselfMentor: getYourSelfMentor,
            IsYourselfReinMentor: getYourSelfReinMentor,
            IsSecurityMentor: userPublicDetail.IsSecurityMentor,
            defaultFiles: defaultFileValue,
          },
        }));
        if (userPublicDetail.Phone !== null) {
          const phoneFieldValue = getPhoneFieldValueFromRawNumber(
            userPublicDetail.Phone
          );
          if (phoneFieldValue) {
            setValue("phoneNumber", phoneFieldValue);
          }
        }
        if (userPublicDetail.PhoneAlt !== null) {
          const phoneFieldValue = getPhoneFieldValueFromRawNumber(
            userPublicDetail.PhoneAlt
          );
          if (phoneFieldValue) {
            setValue("phoneNumberAlt", phoneFieldValue);
          }
        }
        setIsPhoneFiledForceUpdate(!isPhoneFiledForceUpdate);
      }
    });
  };

  const getStateDropDownList = () => {
    const countryId = 1;
    httpGet(
      `${urlConstant.Common.GetStateList}?CountryId=${countryId}`,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        const stateList = response.Value.Data;
        setState((prevState) => ({
          ...prevState,
          isStateDropDownCalled: true,
          publicInfo: {
            ...prevState.publicInfo,
            stateData: stateList,
          },
        }));
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {});
  };

  const getAllDropDownList = async () => {
    const dropDownTypes = [
      dataConstant.AllDropDown.LanguageDropDown,
      dataConstant.AllDropDown.CoutryDropDown,
    ];
    const model = {
      DropDownTypes: dropDownTypes.join(),
    };
    await httpPost(
      urlConstant.Common.GetAllDropDownData,
      model,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        const languageList = response.Value.LanguageDropDownResult.Data;
        const countryList = response.Value.CoutryDropDownResult.Data;
        setState((prevState) => ({
          ...prevState,
          isDropDownApiCalled: true,
          publicInfo: {
            ...prevState.publicInfo,
            languageData: languageList,
          },
          phoneCountryCodeList: countryList
            .filter((item) => !item.isDisabled)
            .map((item) => item.countryCode?.toLowerCase?.() || ""),
        }));
        setIsPhoneFiledForceUpdate(!isPhoneFiledForceUpdate);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {});
  };

  const GetYourselfMentorStatusList = async () => {
    await httpGet(
      urlConstant.Common.GetYourselfMentorStatusList,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        const makeYourselfMentorStatusList = response.Value.Data;
        const makeYourselfReinMentorStatusList = response.Value.Data;
        const makeYourselfSecurityMentorStatusList = response.Value.Data;
        setState((prevState) => ({
          ...prevState,
          isMentorStatusDataCalled: true,
          publicInfo: {
            ...prevState.publicInfo,
            makeYourselfMentorStatusData: makeYourselfReinMentorStatusList,
            makeYourselfReinMentorStatusData: makeYourselfMentorStatusList,
            makeYourselfSecurityMentorStatusData:
              makeYourselfSecurityMentorStatusList,
          },
        }));
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const onSubmit = (data) => {
    if (!isImageValid || Object.keys(errors).length > 0) {
      if (imageUploadRef.current) {
        imageUploadRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      return;
    }
    const finalselectedStates = _.map(data.state, "value")?.join(",");
    const finalselectedLanguage = _.map(data.language, "value")?.join(",");
    const model = {
      FirstName: data.firstName,
      LastName: data.lastName,
      Phone: data.phoneNumber !== "" ? data.phoneNumber.fullNumber : null,
      Company: data.company,
      PhoneAlt:
        data.phoneNumberAlt !== "" ? data.phoneNumberAlt.fullNumber : null,
      // Language: event.values.language ? event.values.language.value : null,
      LanguageIds: finalselectedLanguage,
      StateIds: finalselectedStates,
      IsYourselfMentor:
        data.solarLeadAllowedFlag !== null
          ? Object.keys(data.solarLeadAllowedFlag).length === 0
            ? null
            : data.solarLeadAllowedFlag.value
          : null,
      ToDeleteImage: state.toDeleteImage,
      PublicEmail: data.email.toLowerCase(),
      Description: data.solarDescription,
      ReinDescription: data.reinDescription,
      SecurityDescription: data.securityDescription,
      IsYourselfReinMentor:
        data.rainH2OLeadAllowedFlag !== null
          ? Object.keys(data.rainH2OLeadAllowedFlag).length === 0
            ? null
            : data.rainH2OLeadAllowedFlag.value
          : null,
      IsYourselfSecurityMentor:
        data.securityLeadAllowedFlag !== null
          ? Object.keys(data.securityLeadAllowedFlag).length === 0
            ? null
            : data.securityLeadAllowedFlag.value
          : null,
      FreedomEmail: data.freedomEmail.toLowerCase(),
    };
    const editURL = urlConstant.User.EditPublicInfo;
    const formData = new FormData();
    const { fileProfileIconImage } = state;
    formData.append("file", fileProfileIconImage);
    formData.append("model", JSON.stringify(model));
    console.log(model);
    DocumentUpload(
      editURL,
      formData,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        if (response.Status === dataConstant.APIResponseStatus.Success) {
          showSnackbar(response.description, "success");
          if ((state.isProfileChanged && state.fileProfileIconImage && !state.toDeleteImage) || state.toDeleteImage) {
            const profilePicture = state.toDeleteImage ? null: URL.createObjectURL(state.fileProfileIconImage);
            const updatedUserDetails = {
              ...getLoggedUserDetails,
              UserAccountDetails: {
                ...getLoggedUserDetails.UserAccountDetails,
                ProfilePictureURL: profilePicture,
              },
            };
            dispatch(userDetail({ userDetail: updatedUserDetails }));
          }
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleImageChange = (files) => {
    if (files?.length > 0) {
      setState((prev) => ({
        ...prev,
        imageDisplay: true,
        isProfileChanged: true,
        fileProfileIconImage: files[0],
        toDeleteImage: false,
        publicInfo: {
          ...prev.publicInfo,
          defaultFiles: [
            {
              id: 1,
              images: [URL.createObjectURL(files[0])],
              originalFileName: files[0].name,
            },
          ],
        },
      }));
    }
  };

  const handleDeleteImage = () => {
    setState((prev) => ({
      ...prev,
      fileProfileIconImage: null,
      toDeleteImage: true,
      isProfileChanged: true,
      publicInfo: {
        ...prev.publicInfo,
        profileURL: null,
        defaultFiles: [],
      },
    }));
  };

  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"}>
        <Typography color="secondary.light" variant="h4" fontWeight={600}>
          {t("My Account")}
        </Typography>
        <Box width={"100%"}>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack gap={"20px"}>
              <Alert
                sx={(theme) => ({
                  background: theme.palette.common.errorbg,
                  color: theme.palette.common.errortext,
                })}
                variant="filled"
                severity="info"
              >
                {t("PublicInfo.InformationNotes")}
              </Alert>
              <Alert
                sx={(theme) => ({
                  background: theme.palette.common.warningbg,
                  color: theme.palette.common.errortext,
                })}
                variant="filled"
                severity="info"
              >
                {t("PublicInfo.Notes")}
              </Alert>
              <Typography color="secondary.light" variant="h4" fontWeight={600}>
                {t("PublicInfo.Public Information")}
              </Typography>
              <Card
                ref={imageUploadRef}
                sx={(theme) => ({
                  p: "20px",
                  background: theme.palette.primary.lightdark,
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "20px",
                  flexDirection: "column",
                })}
              >
                <Card
                  sx={(theme) => ({
                    p: {
                      xs: "20px",
                      sm: "25px 30px",
                    },
                    background: theme.palette.background.cardGradient,
                    borderRadius: "20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    gap: 1,
                  })}
                >
                  <CustomImageUpload
                    label="Upload Image"
                    supportedFormats={["jpg", "jpeg", "png"]}
                    onImageChange={handleImageChange}
                    images={
                      state.publicInfo.defaultFiles.length > 0
                        ? [state.publicInfo.defaultFiles[0]]
                        : []
                    }
                    deleteImage={handleDeleteImage}
                    isViewOnly={false} // true to disable upload/delete
                    maxSize={2}
                    isImageValid={handleImageValidation}
                  />
                </Card>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  style={{ width: "100%" }}
                >
                  <Grid
                    container
                    spacing={"20px"}
                    sx={{ flexGrow: 1, width: "100%" }}
                  >
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <CustomTextFieldComponent
                        methods={methods}
                        properties={formConfig.emailParamValueId}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <CustomTextFieldComponent
                        methods={methods}
                        properties={formConfig.companyParamValueId}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <CustomTextFieldComponent
                        methods={methods}
                        properties={formConfig.firstNameParamValueId}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <CustomTextFieldComponent
                        methods={methods}
                        properties={formConfig.lastNameParamValueId}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <CustomPhoneField
                        control={control}
                        name="phoneNumber"
                        label={t("PublicInfo.Phone Number")}
                        required={true}
                        countryList={state.phoneCountryCodeList}
                        isPhoneFiledForceUpdate={isPhoneFiledForceUpdate}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <CustomPhoneField
                        control={control}
                        name="phoneNumberAlt"
                        label={t("PublicInfo.Phone Number Alt")}
                        isAltPhone={true}
                        countryList={state.phoneCountryCodeList}
                        isPhoneFiledForceUpdate={isPhoneFiledForceUpdate}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <CustomTextFieldComponent
                        methods={methods}
                        properties={formConfig.freedomEmailParamValueId}
                      />
                    </Grid>
                  </Grid>
                  {(state.publicInfo.IsMentor ||
                    state.publicInfo.IsReinMentor ||
                    state.publicInfo.IsSecurityMentor) && (
                    <Typography
                      color="secondary.light"
                      variant="h5"
                      fontWeight={600}
                      width={"100%"}
                      my={2}
                    >
                      {t("PublicInfo.Lead information")}
                    </Typography>
                  )}

                  <Stack gap={"20px"}>
                    {(state.publicInfo.IsMentor ||
                      state.publicInfo.IsReinMentor ||
                      state.publicInfo.IsSecurityMentor) && (
                      <Grid
                        container
                        spacing={"20px"}
                        sx={{ flexGrow: 1, width: "100%" }}
                      >
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomMultiSelectDropdown
                            methods={methods}
                            properties={formConfig.languageParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomMultiSelectDropdown
                            methods={methods}
                            properties={formConfig.stateParamValueId}
                          />
                        </Grid>
                      </Grid>
                    )}
                    {state.publicInfo.IsMentor && (
                      <Grid
                        container
                        spacing={"20px"}
                        sx={{
                          flexGrow: 1,
                          width: "100%",
                          display: businessTypeDisabledStatus.Solar
                            ? "none"
                            : "flex",
                        }}
                      >
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <CustomAutoCompleteDropdown
                            methods={methods}
                            properties={formConfig.isBecomeSolarParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <CustomTextFieldComponent
                            methods={methods}
                            properties={formConfig.becomeSolarParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <CustomTextFieldComponent
                            methods={methods}
                            properties={formConfig.noOfSolarLeadParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12 }}>
                          <Typography
                            variant="body2"
                            fontSize={"12px"}
                            color="secondary.text"
                            mb={"4px"}
                          >
                            {t("PublicInfo.Description")}
                          </Typography>
                          <Controller
                            name="solarDescription"
                            control={control}
                            render={({ field }) => (
                              <ReactQuill
                                className="cm-format-fields"
                                theme="snow"
                                modules={modules}
                                formats={formats}
                                {...field}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    )}
                    {state.publicInfo.IsReinMentor && (
                      <Grid
                        container
                        spacing={"20px"}
                        sx={{
                          flexGrow: 1,
                          width: "100%",
                          display: businessTypeDisabledStatus.ReinH2O
                            ? "none"
                            : "flex",
                        }}
                      >
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <CustomAutoCompleteDropdown
                            methods={methods}
                            properties={formConfig.isBecomeReinParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <CustomTextFieldComponent
                            methods={methods}
                            properties={formConfig.becomeReinParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <CustomTextFieldComponent
                            methods={methods}
                            properties={formConfig.noOfReinLeadParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12 }}>
                          <Typography
                            variant="body2"
                            fontSize={"12px"}
                            color="secondary.text"
                            mb={"4px"}
                          >
                            {t("PublicInfo.ReinDescription")}
                          </Typography>
                          <Controller
                            name="reinDescription"
                            control={control}
                            render={({ field }) => (
                              <ReactQuill
                                className="cm-format-fields"
                                theme="snow"
                                modules={modules}
                                formats={formats}
                                {...field}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    )}
                    {state.publicInfo.IsSecurityMentor && (
                      <Grid
                        container
                        spacing={"20px"}
                        sx={{
                          flexGrow: 1,
                          width: "100%",
                          display: businessTypeDisabledStatus.HomeServices
                            ? "none"
                            : "flex",
                        }}
                      >
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <CustomAutoCompleteDropdown
                            methods={methods}
                            properties={formConfig.isBecomeSecurityParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <CustomTextFieldComponent
                            methods={methods}
                            properties={formConfig.becomeSecurityParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <CustomTextFieldComponent
                            methods={methods}
                            properties={formConfig.noOfSecurityLeadParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12 }}>
                          <Typography
                            variant="body2"
                            fontSize={"12px"}
                            color="secondary.text"
                            mb={"4px"}
                          >
                            {t("PublicInfo.HomeServicesDescription")}
                          </Typography>
                          <Controller
                            name="securityDescription"
                            control={control}
                            render={({ field }) => (
                              <ReactQuill
                                className="cm-format-fields"
                                theme="snow"
                                modules={modules}
                                formats={formats}
                                {...field}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Stack>

                  <Box
                    width={"100%"}
                    display={"flex"}
                    justifyContent={"flex-end"}
                    mt={"20px"}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        px: 3,
                      }}
                    >
                      {t("Common.Save")}
                    </Button>
                  </Box>
                </form>
              </Card>
            </Stack>
          </Card>
        </Box>
      </Box>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};
export default PublicInformation;